/** Extensions we can render as an inline image thumbnail. */
const IMAGE_EXTENSIONS = new Set([
  "png",
  "jpg",
  "jpeg",
  "gif",
  "webp",
  "svg",
  "avif",
  "bmp",
  "heic"
]);

/**
 * The lowercase extension of a file, preferring its original name and falling
 * back to the stored URL/path (uploads are renamed to `<uuid>.<ext>`, so the
 * extension survives even when the filename is missing). Returns "" when there
 * is nothing to read one from.
 */
export const getFileExtension = (filename?: string, url?: string): string => {
  const source = filename || url || "";
  // Drop query and hash first: signed URLs carry both, and their values can
  // contain dots that would otherwise be read as the extension.
  const path = source.split(/[?#]/)[0] ?? "";
  const dotIndex = path.lastIndexOf(".");
  if (dotIndex < 0) {
    return "";
  }
  const extension = path.slice(dotIndex + 1).toLowerCase();
  // A dot in a directory segment isn't an extension.
  return extension.includes("/") ? "" : extension;
};

/** Whether a file should be previewed as an image rather than as a document. */
export const isImageFile = (filename?: string, url?: string): boolean =>
  IMAGE_EXTENSIONS.has(getFileExtension(filename, url));

/** Broad file families, each mapped to its own icon and accent by the UI. */
export type FileKind =
  | "image"
  | "document"
  | "spreadsheet"
  | "presentation"
  | "archive"
  | "video"
  | "audio"
  | "other";

const EXTENSIONS_BY_KIND: Record<Exclude<FileKind, "other">, string[]> = {
  image: [...IMAGE_EXTENSIONS],
  document: ["pdf", "doc", "docx", "txt", "rtf", "md", "pages"],
  spreadsheet: ["xls", "xlsx", "csv", "numbers"],
  presentation: ["ppt", "pptx", "key"],
  archive: ["zip", "rar", "7z", "tar", "gz"],
  video: ["mp4", "webm", "mov", "avi", "mkv"],
  audio: ["mp3", "wav", "m4a", "aac", "ogg"]
};

const KIND_BY_EXTENSION = new Map<string, FileKind>(
  Object.entries(EXTENSIONS_BY_KIND).flatMap(([kind, extensions]) =>
    extensions.map(
      (extension) => [extension, kind as FileKind] as [string, FileKind]
    )
  )
);

/** Classifies a file by extension so the UI can pick an icon for it. */
export const getFileKind = (filename?: string, url?: string): FileKind =>
  KIND_BY_EXTENSION.get(getFileExtension(filename, url)) ?? "other";

/**
 * A file size in the shortest human-readable form ("2.4 MB", "812 KB").
 * Sizes below a kilobyte are reported in bytes; anything larger keeps one
 * decimal only while it is needed.
 */
export const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "";
  }
  if (bytes < 1024) {
    return `${Math.round(bytes)} B`;
  }

  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let unitIndex = 0;
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const rounded = value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unitIndex]}`;
};
